// src/components/PersonalLookbook.jsx
//
// Aligned with StyleTryOn.jsx (April 2026):
//   • StyleSnap removed entirely — replaced by StyleTryOn
//   • Reads from users/{uid}/customStyles subcollection (written by StyleTryOn)
//   • "TRY ON" card button opens StyleTryOn modal in-page (quota-gated)
//   • "SHARE WITH BARBER" remains free — it is a data share, not a try-on
//   • Subscription status badge in header
//   • All original share code, fullscreen, and remove logic preserved

import React, { useState, useEffect, useCallback } from 'react';
import {
  doc, onSnapshot, collection, query, orderBy,
  deleteDoc, setDoc, serverTimestamp
} from 'firebase/firestore';
import { db, auth }    from '../firebase';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast           from 'react-hot-toast';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionPage    from './SubscriptionPage';
import StyleTryOn          from './StyleTryon';

const GOLD = '#B8860B';
const DARK = '#001F3F';

export default function PersonalLookbook() {
  const [savedStyles,     setSavedStyles]     = useState([]);
  const [userData,        setUserData]        = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [fullscreenStyle, setFullscreenStyle] = useState(null);
  const [shareCode,       setShareCode]       = useState(null);
  const [isGenerating,    setIsGenerating]    = useState(false);
  const [showSubModal,    setShowSubModal]     = useState(false);

  // StyleTryOn modal state
  const [showTryOn,       setShowTryOn]       = useState(false);
  const [tryOnInitialUrl, setTryOnInitialUrl] = useState(null);
  const [tryOnInitialName,setTryOnInitialName]= useState('');

  const navigate = useNavigate();
  const sub      = useSubscription();

  useEffect(() => {
    if (!auth.currentUser) {
      toast.error('Please login to view your vault.');
      navigate('/login');
      return;
    }

    // Listen to user profile (for headScan URL used in share code)
    const unsubUser = onSnapshot(doc(db, 'users', auth.currentUser.uid), snap => {
      if (snap.exists()) setUserData(snap.data());
    });

    // Read from customStyles subcollection — written by StyleTryOn.jsx
    const stylesQ = query(
      collection(db, 'users', auth.currentUser.uid, 'customStyles'),
      orderBy('createdAt', 'desc')
    );
    const unsubStyles = onSnapshot(stylesQ, snap => {
      setSavedStyles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return () => { unsubUser(); unsubStyles(); };
  }, [navigate]);

  // ── Share code ─────────────────────────────────────────────────────────────
  const generateShareCode = async () => {
    setIsGenerating(true);
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    try {
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 2);
      await setDoc(doc(db, 'share_codes', code), {
        uid:          auth.currentUser.uid,
        userName:     auth.currentUser.displayName || 'Client',
        lookbook:     savedStyles,
        headModelUrl: userData?.headScan || userData?.initialRenderUrl || auth.currentUser.photoURL || null,
        createdAt:    serverTimestamp(),
        expiresAt,
      });
      setShareCode(code);
      toast.success('Access Code Generated!');
    } catch {
      toast.error('Failed to generate code');
    } finally { setIsGenerating(false); }
  };

  const handleNativeShare = async () => {
    const shareUrl  = `${window.location.origin}/barber-view/${shareCode}`;
    const shareText = `ESDRAS | 3D Lookbook Access\n\nAccess Code: ${shareCode}\nLink: ${shareUrl}\n\n(Expires in 2 hours)`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'ESDRAS Lookbook', text: shareText, url: shareUrl });
        toast.success('Shared!');
      } catch (err) {
        if (err.name !== 'AbortError') { navigator.clipboard.writeText(shareText); toast.success('Copied!'); }
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Share details copied!');
    }
  };

  // ── Remove style ───────────────────────────────────────────────────────────
  const removeStyle = async (e, style) => {
    e.stopPropagation();
    try {
      await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'customStyles', style.id));
      toast.success('Style removed from vault');
    } catch { toast.error('Error removing style'); }
  };

  // ── Open StyleTryOn with this style as reference ───────────────────────────
  const handleTryOn = useCallback((e, style) => {
    e.stopPropagation();
    if (!sub.canStyleTryOn.allowed) { setShowSubModal(true); return; }
    setTryOnInitialUrl(style.image || style.imageUrl);
    setTryOnInitialName(style.name || '');
    setShowTryOn(true);
  }, [sub.canStyleTryOn.allowed]);

  if (loading) return (
    <div style={loaderStyle}>
      <div style={spinnerStyle} />
      <p style={{ marginTop: '20px', letterSpacing: '3px', color: GOLD }}>ACCESSING YOUR VAULT</p>
    </div>
  );

  return (
    <div style={container}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* SUBSCRIPTION MODAL */}
      {showSubModal && (
        <SubscriptionPage paywallMode paywallReason="style_exhausted"
          onClose={() => setShowSubModal(false)} redirectAfter="/lookbook" />
      )}

      {/* STYLE TRY-ON MODAL — opens when user clicks TRY ON on a saved style */}
      {showTryOn && (
        <StyleTryOn
          initialStyleUrl={tryOnInitialUrl}
          initialStyleName={tryOnInitialName}
          onClose={() => { setShowTryOn(false); setTryOnInitialUrl(null); }}
        />
      )}

      {/* HEADER */}
      <header style={headerStyle}>
        <div style={topNav}>
          <button onClick={() => navigate(-1)} style={backCircle}>←</button>
          <button onClick={generateShareCode} style={shareBtn}
            disabled={isGenerating || savedStyles.length === 0}>
            {isGenerating ? 'GENERATING...' : 'SHARE WITH BARBER'}
          </button>
        </div>
        <h1 style={pageTitle}>PERSONAL <span style={{ color: '#fff' }}>LOOKBOOK</span></h1>
        <p style={pageSubtitle}>Show your saved styles to a barber or generate a secure share code.</p>

        {!sub.loading && !sub.isSuperAdmin && (
          <div style={subBadge}>
            {sub.isSubscriptionActive
              ? <span style={{ color: GOLD }}>👑 {sub.tier?.toUpperCase()} · Unlimited try-ons</span>
              : <span>
                  FREE · {sub.lifetimeStylesUsed || 0}/1 try-on used ·{' '}
                  <span onClick={() => setShowSubModal(true)}
                    style={{ color: GOLD, cursor: 'pointer', fontWeight: '700' }}>
                    UPGRADE →
                  </span>
                </span>
            }
          </div>
        )}
      </header>

      {/* SHARE CODE OVERLAY */}
      <AnimatePresence>
        {shareCode && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={codeOverlay}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              style={codeCard}>
              <h2 style={{ color: GOLD, margin: 0, fontSize: '1.2rem' }}>BARBER ACCESS CODE</h2>
              <div style={bigCode}>{shareCode}</div>
              <p style={{ opacity: 0.7, fontSize: '0.85rem', marginBottom: '20px' }}>
                Send this directly to your barber via the button below.
              </p>
              <button onClick={handleNativeShare} style={shareSheetBtn}>SEND VIA NATIVE SHARE</button>
              <p style={{ color: '#ff6b6b', fontSize: '0.7rem', marginTop: '15px' }}>
                This code expires in 2 hours
              </p>
              <button onClick={() => setShareCode(null)} style={closeCodeBtn}>CLOSE</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* EMPTY STATE */}
      {savedStyles.length === 0 ? (
        <div style={emptyState}>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🎨</div>
          <h2 style={{ color: GOLD }}>Vault Empty</h2>
          <p style={{ opacity: 0.5, marginBottom: '28px', maxWidth: '360px', textAlign: 'center' }}>
            Use Style Try-On to generate and save AI hairstyle projections to your vault.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={() => {
                setTryOnInitialUrl(null); setTryOnInitialName('');
                setShowTryOn(true);
              }}
              style={primaryBtn}>
              ✨ START STYLE TRY-ON
            </button>
            <button onClick={() => navigate('/gallery')} style={secondaryBtn}>
              BROWSE GLOBAL GALLERY
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ACTION ROW */}
          <div style={actionRow}>
            <button
              onClick={() => { setTryOnInitialUrl(null); setTryOnInitialName(''); setShowTryOn(true); }}
              style={primaryBtn}>
              ✨ NEW STYLE TRY-ON
            </button>
            <button onClick={() => navigate('/gallery')} style={secondaryBtn}>
              BROWSE GALLERY
            </button>
          </div>

          {/* STYLE GRID */}
          <div style={grid}>
            {savedStyles.map((style) => (
              <div key={style.id} style={card} onClick={() => setFullscreenStyle(style)}>
                <div style={imgContainer}>
                  <img src={style.image || style.imageUrl} alt={style.name} style={imgStyle} />
                  <div style={overlayGradient} />

                  {/* Type badge */}
                  <div style={typeBadge}>
                    {style.type === 'style_tryon' ? '✨ AI TRY-ON' : '📌 SAVED'}
                  </div>

                  {/* Remove button */}
                  <div style={cardActions}>
                    <button onClick={e => removeStyle(e, style)} style={trashBtn}>✕</button>
                  </div>
                </div>

                <div style={cardInfo}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={styleNameText}>{style.name}</h3>
                    <p style={creatorText}>
                      {style.source === 'StyleTryOn' ? 'AI Try-On' : style.creatorName || style.source || 'Saved Style'}
                    </p>
                    {style.hairDescription && (
                      <p style={descText}>"{style.hairDescription}"</p>
                    )}
                  </div>
                  {/* TRY ON button — quota-gated */}
                  <button onClick={e => handleTryOn(e, style)} style={tryOnBtn}>
                    {sub.canStyleTryOn.allowed ? 'TRY ON' : '🔒'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* FULLSCREEN OVERLAY */}
      <AnimatePresence>
        {fullscreenStyle && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={fullscreenOverlay} onClick={() => setFullscreenStyle(null)}>
            <div style={fullscreenContent} onClick={e => e.stopPropagation()}>
              <img src={fullscreenStyle.image || fullscreenStyle.imageUrl}
                alt="Zoomed" style={fullImg} />
              <div style={barberModeHeader}>
                <div>
                  <h2 style={{ margin: 0, color: GOLD, fontSize: '1.2rem' }}>
                    {fullscreenStyle.name}
                  </h2>
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                    SHOW THIS TO YOUR STYLIST
                  </p>
                  {fullscreenStyle.hairDescription && (
                    <p style={{ margin: '8px 0 0', fontSize: '0.7rem', fontStyle: 'italic', color: GOLD, opacity: 0.8 }}>
                      "{fullscreenStyle.hairDescription}"
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button onClick={() => handleTryOn({ stopPropagation: () => {} }, fullscreenStyle)}
                    style={{ ...primaryBtn, fontSize: '0.7rem', padding: '10px 18px' }}>
                    ✨ TRY ON AGAIN
                  </button>
                  <button onClick={() => setFullscreenStyle(null)} style={closeFsBtn}>DONE</button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const container      = { minHeight:'100vh', background:`linear-gradient(135deg,${DARK} 0%,#000 100%)`, color:'white', padding:'60px 20px 80px', fontFamily:'Montserrat, sans-serif' };
const loaderStyle    = { height:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:DARK };
const spinnerStyle   = { width:'40px', height:'40px', border:`3px solid ${GOLD}22`, borderTop:`3px solid ${GOLD}`, borderRadius:'50%', animation:'spin 1s linear infinite' };
const headerStyle    = { textAlign:'center', marginBottom:'40px' };
const topNav         = { display:'flex', justifyContent:'space-between', alignItems:'center', maxWidth:'1200px', margin:'0 auto 20px' };
const backCircle     = { background:'rgba(255,255,255,0.05)', border:`1px solid ${GOLD}44`, color:GOLD, width:'40px', height:'40px', borderRadius:'50%', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem' };
const shareBtn       = { background:GOLD, color:'#000', border:'none', padding:'10px 22px', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', fontSize:'0.78rem' };
const pageTitle      = { color:GOLD, fontSize:'2.2rem', fontWeight:'900', letterSpacing:'4px', margin:'0 0 6px' };
const pageSubtitle   = { opacity:0.45, fontSize:'0.85rem' };
const subBadge       = { marginTop:'14px', fontSize:'0.72rem', opacity:0.9 };
const codeOverlay    = { position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' };
const codeCard       = { background:'#0a1a2a', border:`2px solid ${GOLD}`, padding:'40px', borderRadius:'30px', textAlign:'center', maxWidth:'400px', width:'100%' };
const bigCode        = { fontSize:'3.5rem', fontWeight:'900', color:'#fff', letterSpacing:'10px', margin:'20px 0', fontFamily:'monospace' };
const shareSheetBtn  = { background:GOLD, color:'#000', border:'none', padding:'15px', width:'100%', borderRadius:'15px', cursor:'pointer', fontWeight:'bold', fontSize:'0.9rem' };
const closeCodeBtn   = { marginTop:'10px', width:'100%', padding:'15px', background:'rgba(255,255,255,0.05)', border:'none', color:'#fff', borderRadius:'15px', cursor:'pointer' };
const actionRow      = { display:'flex', gap:'12px', maxWidth:'1200px', margin:'0 auto 30px', flexWrap:'wrap' };
const primaryBtn     = { background:GOLD, color:'#000', border:'none', padding:'12px 24px', borderRadius:'50px', fontWeight:'900', cursor:'pointer', fontSize:'0.78rem', whiteSpace:'nowrap' };
const secondaryBtn   = { background:'rgba(255,255,255,0.06)', color:'white', border:'1px solid rgba(255,255,255,0.15)', padding:'12px 22px', borderRadius:'50px', cursor:'pointer', fontSize:'0.75rem', fontWeight:'600', whiteSpace:'nowrap' };
const grid           = { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:'24px', maxWidth:'1200px', margin:'0 auto' };
const card           = { background:'rgba(255,255,255,0.03)', borderRadius:'24px', overflow:'hidden', border:`1px solid rgba(184,134,11,0.15)`, cursor:'pointer', transition:'border-color 0.2s' };
const imgContainer   = { position:'relative', height:'300px' };
const imgStyle       = { width:'100%', height:'100%', objectFit:'cover', display:'block' };
const overlayGradient= { position:'absolute', bottom:0, left:0, right:0, height:'60%', background:'linear-gradient(transparent,rgba(0,0,0,0.85))' };
const typeBadge      = { position:'absolute', top:'12px', left:'12px', background:'rgba(0,0,0,0.75)', color:GOLD, fontSize:'0.5rem', fontWeight:'900', padding:'4px 10px', borderRadius:'20px', letterSpacing:'1px' };
const cardActions    = { position:'absolute', top:'12px', right:'12px', zIndex:2 };
const trashBtn       = { background:'rgba(255,0,0,0.2)', backdropFilter:'blur(10px)', border:'none', color:'#ff6b6b', width:'30px', height:'30px', borderRadius:'50%', cursor:'pointer', fontSize:'0.8rem' };
const cardInfo       = { padding:'18px 20px', display:'flex', alignItems:'flex-start', gap:'12px' };
const styleNameText  = { margin:'0 0 3px', color:GOLD, textTransform:'uppercase', fontSize:'0.95rem', fontWeight:'700', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' };
const creatorText    = { fontSize:'0.65rem', opacity:0.45, margin:0 };
const descText       = { fontSize:'0.6rem', opacity:0.55, margin:'5px 0 0', fontStyle:'italic', lineHeight:1.5, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' };
const tryOnBtn       = { flexShrink:0, padding:'8px 14px', background:'none', border:`1px solid ${GOLD}`, color:GOLD, borderRadius:'10px', fontSize:'0.65rem', fontWeight:'bold', cursor:'pointer', whiteSpace:'nowrap' };
const emptyState     = { textAlign:'center', marginTop:'80px', display:'flex', flexDirection:'column', alignItems:'center' };
const fullscreenOverlay = { position:'fixed', inset:0, background:'rgba(0,0,0,0.98)', zIndex:2000, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'20px' };
const fullscreenContent = { width:'100%', maxWidth:'700px', display:'flex', flexDirection:'column', alignItems:'center' };
const fullImg        = { width:'100%', maxHeight:'65vh', objectFit:'contain', borderRadius:'20px' };
const barberModeHeader = { marginTop:'24px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', borderTop:'1px solid rgba(255,255,255,0.1)', paddingTop:'20px', width:'100%', gap:'16px', flexWrap:'wrap' };
const closeFsBtn     = { background:GOLD, border:'none', padding:'12px 28px', borderRadius:'50px', fontWeight:'bold', cursor:'pointer', fontSize:'0.75rem', whiteSpace:'nowrap' };
